/**
 * Real-Time Conversation Service
 * 
 * Provides WebSocket-based real-time updates for conversation management.
 * Integrates with the existing conversation management system.
 */

import { api } from './api'
import { logger } from '../utils/logger'
import { getValidToken } from '../utils/tokenUtils'
import { API_BASE_URL } from '../utils/constants'

export interface RealTimeConversationUpdate {
  type: 'conversation_update' | 'new_message' | 'status_change' | 'assignment_change'
  conversation_id: string
  data: any
  timestamp: string
}

export interface WebSocketMessage {
  type: string
  data: any
  timestamp: string
}

class RealTimeConversationsService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 3000
  private listeners: Map<string, Set<Function>> = new Map()
  private isConnecting = false
  private heartbeatInterval: NodeJS.Timeout | null = null
  private reconnectTimeout: NodeJS.Timeout | null = null
  private lastConnectionAttempt = 0
  private minReconnectDelay = 2000 // Minimum 2 seconds between connection attempts
  private isManualDisconnect = false
  
  constructor() {
    // Don't auto-connect anymore - wait for explicit initialization after authentication
    logger.info('🚀 WebSocket service initializing - waiting for authentication...')
  }
  
  /**
   * Initialize connection after authentication
   */
  initializeAfterAuth(): void {
    const token = getValidToken()
    if (token && !this.ws) {
      logger.info('🔗 Starting real-time conversations after authentication...')
      this.connect()
    } else if (!token) {
      logger.debug('🔌 Skipping connection - no valid token available')
    }
  }

  /**
   * Connect to WebSocket for real-time conversation updates
   */
  async connect(): Promise<void> {
    const now = Date.now()
    
    // Prevent rapid reconnection attempts
    if (now - this.lastConnectionAttempt < this.minReconnectDelay) {
      logger.warn('⚠️ Too soon for reconnection attempt, waiting...')
      return
    }
    
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      logger.info('⚠️ WebSocket already connected or connecting, skipping')
      return
    }
    
    this.lastConnectionAttempt = now
    this.isConnecting = true
    this.isManualDisconnect = false
    logger.info('🔄 Starting WebSocket connection process...')
    
    try {
      // Get token from the correct location (same as API service)
      let token: string | null = null
      
      try {
        // First try the Zustand auth storage
        const storedState = localStorage.getItem('bgai-auth-storage')
        if (storedState) {
          const parsed = JSON.parse(storedState)
          token = parsed.state?.token
          logger.info('🔍 Token found in bgai-auth-storage', { tokenLength: token?.length })
        }
        
        // Fallback to direct token storage
        if (!token) {
          token = localStorage.getItem('token')
          logger.info('🔍 Token found in direct localStorage', { tokenLength: token?.length })
        }
        
        // Last fallback - check access_token
        if (!token) {
          token = localStorage.getItem('access_token')
          logger.info('🔍 Token found in access_token', { tokenLength: token?.length })
        }
      } catch (error) {
        logger.error('❌ Error parsing auth storage:', error)
      }
      
      if (!token) {
        logger.error('❌ No access token available for WebSocket connection', {
          checkedLocations: ['bgai-auth-storage', 'token', 'access_token'],
          localStorage: {
            'bgai-auth-storage': !!localStorage.getItem('bgai-auth-storage'),
            'token': !!localStorage.getItem('token'),
            'access_token': !!localStorage.getItem('access_token')
          }
        })
        this.isConnecting = false
        this.emit('error', { error: 'No access token' })
        return
      }
      
      // Connect to conversation management WebSocket
      const baseUrl = API_BASE_URL
      const wsUrl = `${baseUrl.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/conversation-management?token=${encodeURIComponent(token)}`
      
      logger.info('🌐 Connecting to real-time conversation updates...', { 
        wsUrl,
        tokenLength: token.length,
        hasToken: !!token
      })
      
      this.ws = new WebSocket(wsUrl)
      
      // Add connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.ws?.readyState === WebSocket.CONNECTING) {
          logger.error('❌ WebSocket connection timeout')
          this.ws?.close()
          this.isConnecting = false
          this.emit('error', { error: 'Connection timeout' })
        }
      }, 10000) // 10 second timeout
      
      this.ws.onopen = () => {
        clearTimeout(connectionTimeout)
        logger.info('✅ Real-time conversation connection established')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.startHeartbeat()
        this.emit('connected', {})
      }
      
      this.ws.onmessage = (event) => {
        try {
          // Handle both string and JSON messages from backend
          let message: WebSocketMessage
          
          if (typeof event.data === 'string') {
            try {
              // Try to parse as JSON first
              message = JSON.parse(event.data)
              
              // 🔧 FIX: Handle malformed type field that comes as character array
              if (message.type && typeof message.type === 'object' && !Array.isArray(message.type)) {
                // Convert character array object back to string
                const typeChars = Object.values(message.type)
                if (typeChars.every(char => typeof char === 'string' && char.length === 1)) {
                  const fixedType = typeChars.join('')
                  logger.info('🔧 Fixed malformed message type from character array', {
                    originalType: message.type,
                    fixedType: fixedType,
                    messageData: message
                  })
                  message.type = fixedType
                }
              }
              
              // 🔧 ADDITIONAL FIX: Handle data field that might also be malformed
              if (message.data && typeof message.data === 'object' && message.data.type && typeof message.data.type === 'object') {
                const dataTypeChars = Object.values(message.data.type)
                if (dataTypeChars.every(char => typeof char === 'string' && char.length === 1)) {
                  message.data.type = dataTypeChars.join('')
                  logger.info('🔧 Fixed malformed data.type from character array', {
                    fixedDataType: message.data.type
                  })
                }
              }
              
            } catch {
              // If not JSON, treat as simple string message
              if (event.data === 'user_disconnected') {
                logger.info('📤 User disconnected message received')
                return // Ignore user disconnect messages
              }
              
              // Create a generic message object for non-JSON strings
              message = {
                type: 'raw_message',
                data: event.data,
                timestamp: new Date().toISOString()
              }
            }
          } else {
            // Handle non-string data
            message = {
              type: 'unknown_data',
              data: event.data,
              timestamp: new Date().toISOString()
            }
          }
          
          // Defensive message handling with extra validation
          if (message && typeof message === 'object') {
            this.handleMessage(message)
          } else {
            logger.warn('⚠️ Invalid message format, skipping:', message)
          }
        } catch (error) {
          // Enhanced error logging to debug the issue
          const errorDetails = {
            rawData: event.data,
            dataType: typeof event.data,
            rawDataLength: event.data?.length,
            rawDataPreview: typeof event.data === 'string' ? event.data.substring(0, 200) : 'Not a string',
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
            errorStack: error instanceof Error ? error.stack : undefined,
            errorName: error instanceof Error ? error.name : 'Unknown',
            isReferenceError: error instanceof ReferenceError,
            isSyntaxError: error instanceof SyntaxError,
            isTypeError: error instanceof TypeError
          }
          
          console.error('❌ DETAILED WebSocket parsing error:', {
            error: error,
            details: errorDetails,
            eventData: event.data,
            timestamp: new Date().toISOString()
          })
          
          logger.error('❌ Failed to process WebSocket message:', error, errorDetails)
          
          // Try to handle the message as a raw string if JSON parsing failed
          try {
            if (typeof event.data === 'string' && event.data.trim()) {
              logger.info('🔧 Attempting to handle as raw string message', {
                data: event.data,
                length: event.data.length
              })
              
              // Try to extract type from raw string patterns
              if (event.data.includes('widget_message_received') || 
                  event.data.includes('widget_typing_start') || 
                  event.data.includes('widget_typing_stop')) {
                logger.info('📨 Detected widget event in raw message, attempting manual parsing')
                
                // Create a basic message structure for widget events
                const message: WebSocketMessage = {
                  type: 'raw_widget_event',
                  data: event.data,
                  timestamp: new Date().toISOString()
                }
                
                this.handleMessage(message)
              }
            }
          } catch (fallbackError) {
            logger.error('❌ Fallback message handling also failed:', fallbackError)
          }
        }
      }
      
      this.ws.onclose = (event) => {
        logger.info('🔌 Real-time conversation connection closed', { 
          code: event.code, 
          reason: event.reason,
          isManualDisconnect: this.isManualDisconnect
        })
        this.isConnecting = false
        this.stopHeartbeat()
        
        // Always try to reconnect for SaaS reliability (like Crisp/Intercom)
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          logger.info('🔄 Connection lost - attempting automatic reconnection...')
          this.scheduleReconnect()
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          logger.warn('⚠️ Max reconnection attempts reached - will retry in 30 seconds')
          setTimeout(() => {
            this.reconnectAttempts = 0 // Reset attempts
            this.connect()
          }, 30000)
        }
        
        this.emit('disconnected', { code: event.code, reason: event.reason })
      }
      
      this.ws.onerror = (error) => {
        clearTimeout(connectionTimeout)
        logger.error('❌ Real-time conversation WebSocket error:', error)
        this.isConnecting = false
        this.emit('error', { error: 'WebSocket connection failed' })
      }
      
    } catch (error) {
      logger.error('❌ Failed to establish real-time connection:', error)
      this.isConnecting = false
      this.emit('error', { error: 'Failed to create WebSocket' })
      this.scheduleReconnect()
    }
  }
  
  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.isManualDisconnect = true
    this.stopHeartbeat()
    
    // Clear any pending reconnection
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect')
      this.ws = null
    }
    
    // Don't clear listeners on disconnect - allow reconnection
    logger.info('🔌 Disconnected from real-time conversation updates')
  }
  
  /**
   * Send message to WebSocket
   */
  private send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    } else {
      logger.warn('⚠️ Attempted to send message but WebSocket not connected:', message)
    }
  }
  
  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    try {
      logger.debug('📨 Real-time conversation message received:', message)
      
      // Validate message structure - allow both WebSocket messages and direct message objects
      if (!message || typeof message !== 'object') {
        logger.warn('⚠️ Invalid message structure:', message)
        return
      }
      
      // Handle direct message objects (agent messages) that don't have a type field
      if (!message.type && message.id && message.conversation_id && message.role) {
        logger.info('📨 Processing direct message object (agent message)', {
          messageId: message.id,
          conversationId: message.conversation_id,
          role: message.role,
          content: message.content
        })
        
        // Convert direct message object to conversation_list_update format
        const formattedMessage = message.role === 'user' || message.role === 'customer' 
          ? `Customer: ${message.content}` 
          : message.role === 'agent'
            ? `Agent: ${message.content}`
            : `Message: ${message.content}`
        
        this.emit('conversation_list_update', {
          type: 'new_message',
          conversation_id: message.conversation_id,
          last_message: formattedMessage,
          last_message_time: message.timestamp,
          unread_count: message.role === 'user' || message.role === 'customer' ? 1 : 0 // 1 = increment, 0 = no change
        })
        
        // 🎯 CRITICAL: If this is an agent message, also reset unread count
        if (message.role === 'agent') {
          this.emit('conversation_list_update', {
            type: 'messages_read',
            conversation_id: message.conversation_id,
            unread_count: 0,
            timestamp: message.timestamp
          })
        }
        
        return
      }
      
      // Validate WebSocket message structure
      if (!message.type) {
        logger.warn('⚠️ Invalid WebSocket message structure - missing type:', message)
        return
      }
    
    switch (message.type) {
      case 'connected':
        logger.info('✅ WebSocket connection confirmed by server')
        this.emit('connected', message.data)
        break
        
      case 'subscription_confirmed':
        logger.info('✅ Subscription confirmed:', message.data)
        break
        
      case 'conversation_update':
        this.emit('conversation_update', message.data)
        
        // 🎯 Also emit assignment_change if assignment-related fields changed
        const hasAssignmentChange = message.data?.assigned_agent_id !== undefined || 
                                  message.data?.assigned_agent_name !== undefined ||
                                  message.data?.status !== undefined
        
        if (hasAssignmentChange) {
          logger.info('🔄 Emitting assignment_change from conversation_update', {
            conversation_id: message.data?.conversation_id,
            status: message.data?.status,
            assigned_agent_name: message.data?.assigned_agent_name
          })
          this.emit('assignment_change', message.data)
        }
        break
        
      case 'new_message':
        // Handle agent messages for real-time sidebar updates
        logger.info('📨 New message received, updating conversation list', {
          conversationId: message.data?.conversation_id,
          messageContent: message.data?.content || message.data?.message,
          senderType: message.data?.sender_type || message.data?.role,
          timestamp: message.data?.timestamp
        })
        
        // Emit as conversation_list_update to trigger sidebar refresh for agent messages
        if (message.data?.conversation_id) {
          const content = message.data.content || message.data.message || ""
          const senderType = message.data.sender_type || message.data.role || 'agent'
          const formattedMessage = senderType === 'user' || senderType === 'customer' 
            ? `Customer: ${content}` 
            : `Agent: ${content}`
          
          this.emit('conversation_list_update', {
            type: 'new_message',
            conversation_id: message.data.conversation_id,
            last_message: formattedMessage,
            last_message_time: message.data.timestamp,
            unread_count: 0 // Agent messages don't increase unread count
          })
        }
        
        this.emit('new_message', message.data)
        break
        
      case 'status_change':
        this.emit('status_change', message.data)
        break
        
      case 'assignment_change':
        this.emit('assignment_change', message.data)
        break
        
      case 'conversation_list_update':
        this.emit('conversation_list_update', message.data)
        break
        
      case 'typing_start':
        this.emit('typing_start', message.data)
        break
        
      case 'typing_stop':
        this.emit('typing_stop', message.data)
        break
        
      case 'widget_typing_start':
        // Handle customer typing indicators from widget
        logger.debug('⌨️ Customer started typing', message.data)
        this.emit('typing_start', message.data)
        break
        
      case 'widget_typing_stop':
        // Handle customer typing indicators from widget
        logger.debug('⌨️ Customer stopped typing', message.data)
        this.emit('typing_stop', message.data)
        break
        
      case 'agent_presence_update':
        this.emit('agent_presence_update', message.data)
        break
        
      case 'heartbeat':
        this.send({ type: 'heartbeat_response', timestamp: Date.now() })
        break
        
      case 'heartbeat_response':
        logger.debug('💓 Heartbeat response received')
        break
        
      case 'widget_message_received':
        // Handle widget messages from customers for real-time sidebar updates
        logger.info('📨 Widget message received, updating conversation list', {
          conversationId: message.conversation_id,
          messageContent: message.message_content,
          timestamp: message.timestamp
        })
        
        // Emit as conversation_list_update to trigger sidebar refresh
        this.emit('conversation_list_update', {
          type: 'new_message',
          conversation_id: message.conversation_id,
          last_message: `Customer: ${message.message_content}`,
          last_message_time: message.timestamp,
          unread_count: 1 // Always increment by 1 for customer messages
        })
        break
        
      case 'message_received':
        // Handle general message_received events (both agent and customer messages)
        logger.info('📨 General message received, updating conversation list', {
          conversationId: message.conversation_id,
          messageContent: message.content || message.data?.content,
          senderType: message.source || message.data?.source || message.data?.role,
          timestamp: message.timestamp
        })
        
        // Emit as conversation_list_update to trigger sidebar refresh
        if (message.conversation_id) {
          const content = message.content || message.data?.content || message.data?.message || ""
          const senderType = message.source || message.data?.source || message.data?.role || 'unknown'
          const formattedMessage = senderType === 'user' || senderType === 'customer' 
            ? `Customer: ${content}` 
            : senderType === 'agent'
              ? `Agent: ${content}`
              : `Message: ${content}`
          
          this.emit('conversation_list_update', {
            type: 'new_message',
            conversation_id: message.conversation_id,
            last_message: formattedMessage,
            last_message_time: message.timestamp,
            unread_count: senderType === 'user' || senderType === 'customer' ? 1 : 0
          })
        }
        break
        
      case 'raw_message':
        logger.debug('📝 Raw message received:', message.data)
        break
        
      case 'raw_widget_event':
        // Handle malformed widget events that couldn't be parsed as JSON
        logger.info('🔧 Processing raw widget event', { data: message.data })
        
        try {
          // Try to manually extract conversation ID and trigger a basic update
          const rawData = String(message.data)
          const conversationIdMatch = rawData.match(/conversation_id['":\s]*([a-zA-Z0-9-]+)/)
          
          if (conversationIdMatch) {
            const conversationId = conversationIdMatch[1]
            logger.info('📨 Extracted conversation ID from raw widget event', { conversationId })
            
            // Emit a basic conversation list update to trigger refresh
            this.emit('conversation_list_update', {
              type: 'raw_widget_message',
              conversation_id: conversationId,
              timestamp: message.timestamp
            })
          }
        } catch (parseError) {
          logger.warn('⚠️ Could not parse raw widget event', { error: parseError, data: message.data })
        }
        break
        
      case 'customer_ended_chat':
        // Handle customer ended chat events
        logger.info('🔚 Customer ended chat session', message.data)
        this.emit('conversation_list_update', {
          type: 'customer_ended_chat',
          conversation_id: message.conversation_id,
          data: message.data,
          timestamp: message.timestamp
        })
        break
        
      case 'widget_connection_ended':
        // Handle widget connection ended events
        logger.info('🔌 Widget connection ended', message.data)
        this.emit('conversation_list_update', {
          type: 'widget_connection_ended',
          conversation_id: message.conversation_id,
          data: message.data,
          timestamp: message.timestamp
        })
        break
        
      case 'widget_visitor_disconnected':
        // Handle widget visitor disconnected events
        logger.info('🔌 Widget visitor disconnected', message)
        this.emit('conversation_list_update', {
          type: 'widget_visitor_disconnected',
          conversation_id: message.conversation_id,
          data: {
            visitor_name: message.visitor_name,
            session_id: message.session_id,
            timestamp: message.timestamp
          },
          timestamp: message.timestamp
        })
        break
        
      case 'conversation_status_update':
        // Handle conversation status updates (like resolved)
        logger.info('📊 Conversation status updated', message.data)
        this.emit('conversation_list_update', {
          type: 'conversation_status_update',
          conversation_id: message.conversation_id,
          data: message.data,
          timestamp: message.timestamp
        })
        break
        
      case 'unread_count_update':
        // Handle unread count updates
        logger.debug('📊 Unread count updated', message.data)
        this.emit('unread_count_update', {
          conversation_id: message.conversation_id,
          unread_count: message.unread_count,
          data: message.data,
          timestamp: message.timestamp
        })
        break
        
      case 'unknown_data':
        logger.warn('⚠️ Unknown data type received:', message.data)
        break
        
      default:
        logger.warn('⚠️ Unknown real-time message type:', message.type, message)
    }
    
    } catch (error) {
      // Enhanced error logging for handleMessage
      const errorInfo = {
        messageType: message?.type,
        messageData: message,
        errorName: error instanceof Error ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      }
      
      console.error('❌ DETAILED handleMessage error:', {
        error: error,
        info: errorInfo,
        message: message
      })
      
      logger.error('❌ Error in handleMessage:', error, errorInfo)
    }
  }
  
  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'heartbeat', timestamp: Date.now() })
    }, 30000) // Every 30 seconds
  }
  
  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }
  
  /**
   * Schedule reconnection attempt with better debouncing
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('❌ Max reconnection attempts reached')
      return
    }
    
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }
    
    this.reconnectAttempts++
    const delay = Math.max(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      this.minReconnectDelay
    ) // Exponential backoff with minimum delay
    
    logger.info(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`)
    
    this.reconnectTimeout = setTimeout(() => {
      if (!this.isManualDisconnect) {
        this.connect()
      }
    }, delay)
  }
  
  /**
   * Subscribe to conversation updates
   */
  subscribeToConversation(conversationId: string): void {
    this.send({
      type: 'subscribe_conversation',
      conversation_id: conversationId
    })
  }
  
  /**
   * Unsubscribe from conversation updates
   */
  unsubscribeFromConversation(conversationId: string): void {
    this.send({
      type: 'unsubscribe_conversation',
      conversation_id: conversationId
    })
  }
  
  /**
   * Subscribe to conversation list updates
   */
  subscribeToConversationList(): void {
    this.send({
      type: 'subscribe_conversation_list'
    })
  }
  
  /**
   * Subscribe to agent presence updates
   */
  subscribeToAgentPresence(): void {
    this.send({
      type: 'subscribe_agent_presence'
    })
  }
  
  /**
   * Add event listener
   */
  addEventListener(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)
  }
  
  /**
   * Remove event listener
   */
  removeEventListener(event: string, callback: Function): void {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      eventListeners.delete(callback)
      if (eventListeners.size === 0) {
        this.listeners.delete(event)
      }
    }
  }
  
  /**
   * Emit event to listeners
   */
  private emit(event: string, data: any): void {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          logger.error(`❌ Error in event listener for ${event}:`, error)
        }
      })
    }
  }
  
  /**
   * Get connection status
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }
  
  /**
   * Get connection state
   */
  get connectionState(): string {
    if (!this.ws) return 'disconnected'
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting'
      case WebSocket.OPEN: return 'connected'
      case WebSocket.CLOSING: return 'closing'
      case WebSocket.CLOSED: return 'disconnected'
      default: return 'unknown'
    }
  }
}

// Export singleton instance
export const realTimeConversationsService = new RealTimeConversationsService()

export default realTimeConversationsService